import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { SubmitHandler, useForm } from "react-hook-form";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Spinner,
} from "reactstrap";

import useAlertBar from "@/hooks/useAlertBar";
import InputGroup from "../common/input-group";
import { changePasswordService } from "@/services/authService";
import { postLoginHandoff, TokenBundle } from "./postLoginHandoff";
import { routes } from "@/utils/routes";

const PWD_STORAGE_KEY = "sv_pwd_change";

// Policy regex (verbatim from spec): 8+ chars, upper, lower, digit, special @$!%*?&
const PASSWORD_REGEX = /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[@$!%*?&])([a-zA-Z0-9@$!%*?&]{8,})$/;

const validationSchema = yup.object({
  oldPassword: yup.string().required("Current password is required"),
  newPassword: yup
    .string()
    .required("New password is required")
    .matches(
      PASSWORD_REGEX,
      "Password must be at least 8 characters and contain uppercase, lowercase, a number, and a special character (@$!%*?&)"
    ),
  confirmPassword: yup
    .string()
    .required("Please confirm your new password")
    .oneOf([yup.ref("newPassword")], "Passwords do not match"),
});

type Inputs = {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
};

// Live rule checklist helpers
function hasOtherChars(password: string): boolean {
  return password.length > 0 && !/^[a-zA-Z0-9@$!%*?&]*$/.test(password);
}

const rules = [
  { label: "At least 8 characters", test: (p: string) => p.length >= 8 },
  { label: "Uppercase letter (A–Z)", test: (p: string) => /[A-Z]/.test(p) },
  { label: "Lowercase letter (a–z)", test: (p: string) => /[a-z]/.test(p) },
  { label: "Number (0–9)", test: (p: string) => /[0-9]/.test(p) },
  { label: "Special character (@$!%*?&)", test: (p: string) => /[@$!%*?&]/.test(p) },
];

export default function ChangePasswordRequired() {
  const router = useRouter();
  const { openAlertBar } = useAlertBar();
  const [tokenBundle, setTokenBundle] = useState<TokenBundle | null>(null);
  const [loading, setLoading] = useState(false);
  const [showMfaInterstitial, setShowMfaInterstitial] = useState(false);
  const [watchedPassword, setWatchedPassword] = useState("");

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<Inputs>({
    resolver: yupResolver(validationSchema),
    criteriaMode: "all",
    shouldFocusError: true,
  });

  // Keep password value in state for the live checklist
  const newPasswordValue = watch("newPassword", "");

  useEffect(() => {
    setWatchedPassword(newPasswordValue);
  }, [newPasswordValue]);

  useEffect(() => {
    const raw = sessionStorage.getItem(PWD_STORAGE_KEY);
    if (!raw) {
      router.replace(routes.login);
      return;
    }
    try {
      setTokenBundle(JSON.parse(raw));
    } catch {
      router.replace(routes.login);
    }
  }, [router]);

  const handleHandoff = () => {
    if (tokenBundle) {
      sessionStorage.removeItem(PWD_STORAGE_KEY);
      postLoginHandoff(tokenBundle);
    }
  };

  const onSubmit: SubmitHandler<Inputs> = async (formData) => {
    if (!tokenBundle) return;
    setLoading(true);
    try {
      const { data } = await changePasswordService(
        { oldPassword: formData.oldPassword, newPassword: formData.newPassword },
        tokenBundle.accessTokenString
      );
      if (data.status) {
        openAlertBar(data.message || "Password updated successfully.", true);
        setShowMfaInterstitial(true);
      } else {
        openAlertBar(data.message || "Password change failed. Please try again.", false);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      openAlertBar(
        "Unexpected error occurred while changing your password. Please try again.",
        false
      );
      console.log(error);
    }
  };

  if (showMfaInterstitial) {
    return (
      <Container className="my-5">
        <Col sm={6} xs={12} className="mx-auto">
          <Card>
            <CardBody className="text-center py-5">
              <h4 className="mb-3">Password Updated</h4>
              <p className="text-muted mb-4">
                Secure your account with two-factor authentication — you can set it
                up under <strong>Settings &rarr; Security</strong> in the dashboard.
              </p>
              <Button className="w-100 mb-2" onClick={handleHandoff}>
                CONTINUE TO DASHBOARD
              </Button>
              <Button color="link" className="w-100" onClick={handleHandoff}>
                Remind me later
              </Button>
            </CardBody>
          </Card>
        </Col>
      </Container>
    );
  }

  return (
    <Container className="my-5">
      <Col sm={6} xs={12} className="mx-auto">
        <Card>
          <CardBody>
            <Form
              onSubmit={handleSubmit(onSubmit)}
              className="my-4 needs-validation"
            >
              <h2 className="text-center mb-2">Update Your Password</h2>
              <p className="text-center text-muted mb-4" style={{ fontSize: 14 }}>
                Your password needs to be updated before you continue.
              </p>

              <InputGroup
                required
                label="Current Password"
                type="password"
                name="oldPassword"
                register={register}
                error={errors.oldPassword?.message}
              />
              <InputGroup
                required
                label="New Password"
                type="password"
                name="newPassword"
                register={register}
                error={errors.newPassword?.message}
              />
              <InputGroup
                required
                label="Confirm New Password"
                type="password"
                name="confirmPassword"
                register={register}
                error={errors.confirmPassword?.message}
              />

              {/* Live password rule checklist */}
              {watchedPassword.length > 0 && (
                <div className="mb-3 p-3 rounded" style={{ background: "#f8f9fa", fontSize: 13 }}>
                  {rules.map((rule) => {
                    const passed = rule.test(watchedPassword);
                    return (
                      <div key={rule.label} className={passed ? "text-success" : "text-danger"}>
                        {passed ? "✓" : "✗"} {rule.label}
                      </div>
                    );
                  })}
                  {hasOtherChars(watchedPassword) && (
                    <div className="text-warning mt-1">
                      ⚠ Contains characters outside the allowed set (only @$!%*?& are accepted as special characters)
                    </div>
                  )}
                </div>
              )}

              <Button type="submit" disabled={loading} className="w-100">
                CHANGE PASSWORD
                {loading && <Spinner size="sm" className="ms-3" />}
              </Button>
            </Form>
          </CardBody>
        </Card>
      </Col>
    </Container>
  );
}
